import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { UserRound, UserPlus, ShieldCheck, Globe } from 'lucide-react';
import { toast } from 'sonner';
import useDebounce from '../../hooks/useDebounce';

import { api } from '../../services/client';
import { useTranslation } from '../../i18n';
import useModal from '../../hooks/useModal';
import {
  useSpaceDetail,
  useUpdateSpaceAdmins,
  useRemoveSpaceMember,
} from '../../hooks/queries/useSpaceQueries';
import { getImageUrl } from '../../utils/imageUrl';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import MemberListLayout, { IMemberRow } from '../shared/MemberListLayout';
import AddMemberDialog from './AddMemberDialog';

interface ISpaceMembersPaginated {
  members: IMemberRow[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  adminCount: number;
  activeCount: number;
}

const SpaceMemberList = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: spaceDetail } = useSpaceDetail(id || '');

  const [members, setMembers] = useState<IMemberRow[]>([]);
  const [, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [adminCount, setAdminCount] = useState(0);
  const [activeCount, setActiveCount] = useState(0);
  const [totalMemberCount, setTotalMemberCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const addMemberDialog = useModal();
  const updateSpaceAdmins = useUpdateSpaceAdmins();
  const removeSpaceMember = useRemoveSpaceMember();
  const limit = 20;

  const loadMembers = async () => {
    if (!id) return;
    setLoading(true);

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (search) params.set('search', search);
      if (roleFilter !== 'all') params.set('role', roleFilter);

      const data = await api.get<ISpaceMembersPaginated>(
        `/backoffice/spaces/${id}/members-paginated?${params}`,
      );
      setMembers(data.members);
      setTotal(data.total);
      setTotalPages(data.totalPages);

      // Only update stats on unfiltered requests
      if (!search && roleFilter === 'all') {
        setAdminCount(data.adminCount);
        setActiveCount(data.activeCount);
        setTotalMemberCount(data.activeCount);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMembers();
  }, [id, page, roleFilter]);

  useDebounce(
    () => {
      setPage(1);
      loadMembers();
    },
    300,
    [search],
  );

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'owner':
        return (
          <Badge variant="info" className="rounded-full">
            {t('spaces.role_owner')}
          </Badge>
        );
      case 'admin':
        return (
          <Badge variant="purple" className="rounded-full">
            {t('spaces.role_admin')}
          </Badge>
        );
      default:
        return (
          <Badge variant="muted" className="rounded-full">
            {t('spaces.role_member')}
          </Badge>
        );
    }
  };

  return (
    <>
      <MemberListLayout
        onBack={() => navigate(`/spaces/${id}`)}
        backLabel={t('spaces.back_to_detail')}
        headerIcon={
          <div className="w-12 h-12 rounded-lg bg-blue-500 flex items-center justify-center text-white overflow-hidden shrink-0">
            {spaceDetail?.avatar ? (
              <img
                src={getImageUrl(spaceDetail.avatar)!}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              <Globe className="w-6 h-6" />
            )}
          </div>
        }
        title={`${spaceDetail?.name || '—'} - ${t('spaces.member_list_title')}`}
        subtitle={t('spaces.total_count', {
          count: totalMemberCount.toString(),
        })}
        headerAction={
          <Button
            variant="default"
            onClick={() => addMemberDialog.open()}
            className="inline-flex items-center gap-1.5"
          >
            <UserPlus className="w-4 h-4" /> {t('spaces.add_member')}
          </Button>
        }
        stats={[
          {
            label: t('spaces.total_member_count'),
            value: totalMemberCount.toString(),
            icon: <UserRound className="w-8 h-8 text-blue-600" />,
            bg: '',
          },
          {
            label: t('spaces.admin_count'),
            value: adminCount.toString(),
            icon: <ShieldCheck className="w-8 h-8 text-purple-600" />,
            bg: '',
          },
          {
            label: t('spaces.active_member_count'),
            value: activeCount.toString(),
            icon: <UserRound className="w-8 h-8 text-green-600" />,
            bg: '',
          },
        ]}
        searchPlaceholder={t('spaces.search_member_placeholder')}
        search={search}
        onSearchChange={setSearch}
        roleFilter={roleFilter}
        onRoleFilterChange={(role) => {
          setRoleFilter(role);
          setPage(1);
        }}
        filterLabels={{
          all: t('spaces.filter_all_members'),
          admin: t('spaces.filter_admins'),
          member: t('spaces.filter_members'),
        }}
        members={members}
        loading={loading}
        getRoleBadge={getRoleBadge}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        onViewUser={(member) => navigate(`/users/${member.userId}`)}
        onToggleAdmin={async (member) => {
          if (!id) return;

          const isAdmin = member.role === 'admin';
          const name = member.user?.displayName || member.user?.username || '';
          const msg = isAdmin
            ? t('members.confirm_remove_admin', { name })
            : t('members.confirm_set_admin', { name });

          if (!window.confirm(msg)) return;

          try {
            // Get current admins, toggle this user
            const currentAdmins = await api.get<{ id: string }[]>(
              `/backoffice/spaces/${id}/admins`,
            );
            const currentIds = currentAdmins.map((a) => a.id);
            const newIds = isAdmin
              ? currentIds.filter((aid) => aid !== member.userId)
              : [...currentIds, member.userId];

            updateSpaceAdmins.mutate(
              { spaceId: id!, userIds: newIds },
              { onSuccess: () => loadMembers() },
            );
          } catch (e: any) {
            toast.error(e.message || t('common.operation_failed'));
          }
        }}
        onRemoveMember={async (member) => {
          if (!id) return;

          const name = member.user?.displayName || member.user?.username || '';

          if (!window.confirm(t('members.confirm_remove_member', { name })))
            return;

          removeSpaceMember.mutate(
            { spaceId: id!, userId: member.userId },
            { onSuccess: () => loadMembers() },
          );
        }}
      />
      {spaceDetail && (
        <AddMemberDialog
          isOpen={addMemberDialog.isOpen}
          onClose={() => {
            addMemberDialog.close();
            loadMembers();
          }}
          space={spaceDetail}
        />
      )}
    </>
  );
};

export default SpaceMemberList;
